// @refresh reset
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User, Session } from '@supabase/supabase-js'
import {
  clearStoredAuthState,
  logout as authLogout,
  resolveRoleForUser,
} from '@lib/auth'
import { supabase } from '@lib/supabase/client'
import { analyticsService } from '@lib/analytics/posthog.service'

interface AuthContextType {
  user: User | null
  session: Session | null
  role: 'patient' | 'provider' | null
  isLoading: boolean
  isAuthenticated: boolean
  logout: () => Promise<void>
  setUser: (user: User | null) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [role, setRole] = useState<'patient' | 'provider' | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession()

        if (!currentSession) {
          clearStoredAuthState()
          setUser(null)
          setSession(null)
          setRole(null)
          return
        }

        if ((currentSession.expires_at ? currentSession.expires_at * 1000 : 0) <= Date.now()) {
          clearStoredAuthState()
          await supabase.auth.signOut({ scope: 'local' })
          setUser(null)
          setSession(null)
          setRole(null)
          return
        }

        const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser()
        if (userError || !currentUser) {
          clearStoredAuthState()
          await supabase.auth.signOut({ scope: 'local' })
          setUser(null)
          setSession(null)
          setRole(null)
          return
        }

        setUser(currentUser)
        setSession(currentSession)
        const resolvedRole = await resolveRoleForUser(currentUser)
        setRole(resolvedRole)
        analyticsService.identifyAuthenticatedUser(currentUser.id, {
          user_role: resolvedRole || 'unknown',
          platform: 'web',
        })
      } catch (error) {
        console.error('Auth initialization error:', error)
        clearStoredAuthState()
        setUser(null)
        setSession(null)
        setRole(null)
      } finally {
        setIsLoading(false)
      }
    }

    initializeAuth()

    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      setSession(newSession)
      if (event === 'SIGNED_OUT') {
        setUser(null)
        setRole(null)
        clearStoredAuthState()
        analyticsService.resetUserSession()
      } else if (newSession?.user) {
        setUser(newSession.user)
        const resolvedRole = await resolveRoleForUser(newSession.user)
        setRole(resolvedRole)
        analyticsService.identifyAuthenticatedUser(newSession.user.id, {
          user_role: resolvedRole || 'unknown',
          platform: 'web',
        })
      } else {
        setUser(null)
        setRole(null)
        clearStoredAuthState()
      }

      setIsLoading(false)
    })

    return () => {
      authListener?.subscription.unsubscribe()
    }
  }, [])

  const handleLogout = async () => {
    setUser(null)
    setSession(null)
    setRole(null)
    setIsLoading(false)
    analyticsService.resetUserSession()

    try {
      await authLogout()
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const value: AuthContextType = {
    user,
    session,
    role,
    isLoading,
    isAuthenticated: !!user && !!session,
    logout: handleLogout,
    setUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
